import type { EventBootstrapResponse } from '@irace/shared';
import { useEffect, useState } from 'react';
import { useServiceWorkerUpdate } from '../pwa/useServiceWorker.ts';
import { formatOpensAt } from '../lib/format.ts';
import { CheckinAccepted } from './CheckinAccepted.tsx';
import { CheckinForm } from './CheckinForm.tsx';
import { CheckoutResumeScreen } from './CheckoutPanel.tsx';
import { enabledCheckout, resumeTakesOver } from './checkoutResume.ts';
import type { LocationIntent } from './locationIntent.ts';
import { PendingScreen } from './PendingScreen.tsx';
import { RecoveryBanner, SubmissionStatus, UpdatePrompt } from './SubmissionStatus.tsx';
import { useCheckinFlow } from './useCheckinFlow.ts';
import { chooseLocationMode } from './validation.ts';

/** §15 — the pending screen tells the participant whether a retry can work. */
function useOnlineStatus(): boolean {
  const [online, setOnline] = useState(() => navigator.onLine);

  useEffect(() => {
    const update = () => {
      setOnline(navigator.onLine);
    };
    window.addEventListener('online', update);
    window.addEventListener('offline', update);
    return () => {
      window.removeEventListener('online', update);
      window.removeEventListener('offline', update);
    };
  }, []);

  return online;
}

/**
 * The form plus the submit state machine (§11).
 *
 * Split out of CheckinPage so the outbox, Turnstile widget and retry engine
 * are only constructed once the event is known to be open for check-in.
 */
export function CheckinExperience({
  bootstrap,
  intent,
}: {
  bootstrap: EventBootstrapResponse;
  intent: LocationIntent;
}) {
  const flow = useCheckinFlow(bootstrap);
  const isOnline = useOnlineStatus();
  // An event with no real location never asked for one, so neither the
  // pending nor the accepted screen names the placeholder it submitted.
  const showLocation = chooseLocationMode(bootstrap.locations).mode !== 'hidden';
  const { updateReady, applyUpdate } = useServiceWorkerUpdate();

  const { state } = flow;
  // A submission this participant is waiting on takes the whole screen (§15);
  // a record recovered from a previous visit is a banner on a usable form
  // (§18), because they may be here to check someone else in.
  const takeOverScreen =
    !flow.recovered && (state.kind === 'pending' || state.kind === 'verify_retry');
  const inFlight = state.kind === 'submitting' || flow.retrying;

  // G-014 — the same gate the engine uses, so the button can never exist
  // without a store, a service and a retry loop behind it.
  const checkout = enabledCheckout(bootstrap.checkout);
  // A device returning to an event it already checked into opens on the
  // confirmation instead of an empty form — but never over a form the
  // participant has started filling in, and never over a submission they are
  // waiting on. The decision is made here, at render, because the candidate
  // arrives asynchronously and both of those can become true after it does.
  //
  // With check-out on, that confirmation is the resume screen, because there
  // is something left to do on it. Without it, the same accepted record still
  // has something to say: this person is already checked in, and a blank form
  // would have them do it twice.
  const resume = resumeTakesOver({
    candidate: flow.resumable,
    formDirty: flow.formDirty,
    stateKind: state.kind,
  })
    ? flow.resumable
    : null;

  const screen =
    state.kind === 'accepted' ? (
      <CheckinAccepted
        locationName={showLocation ? state.record.locationName : null}
        organization={bootstrap.organization}
        eventUrl={bootstrap.event.event_url}
        checkout={checkout}
        checkoutState={flow.checkoutState}
        onCheckout={flow.checkout}
      />
    ) : resume && !checkout ? (
      <CheckinAccepted
        locationName={showLocation ? resume.locationName : null}
        checkedInAt={formatOpensAt(resume.clientCreatedAt, bootstrap.event.timezone)}
        organization={bootstrap.organization}
        eventUrl={bootstrap.event.event_url}
        checkoutState={flow.checkoutState}
        onCheckout={flow.checkout}
        onCheckinAgain={flow.reset}
      />
    ) : resume && checkout ? (
      <CheckoutResumeScreen
        checkedInAt={formatOpensAt(resume.clientCreatedAt, bootstrap.event.timezone)}
        locationName={showLocation ? resume.locationName : null}
        checkout={checkout}
        state={flow.checkoutState}
        onCheckout={flow.checkout}
        onCheckinAgain={flow.reset}
        organization={bootstrap.organization}
      />
    ) : takeOverScreen && (state.kind === 'pending' || state.kind === 'verify_retry') ? (
      <PendingScreen
        variant={state.kind}
        locationName={showLocation ? state.record.locationName : null}
        pendingCount={flow.pendingCount}
        isOnline={isOnline}
        retrying={flow.retrying}
        onRetry={flow.retryNow}
      />
    ) : (
      <CheckinForm
        bootstrap={bootstrap}
        intent={intent}
        submitting={state.kind === 'submitting'}
        onSubmit={flow.submit}
        onTouched={flow.markFormTouched}
      >
        {flow.recovered && flow.pendingCount > 0 ? (
          <RecoveryBanner
            pendingCount={flow.pendingCount}
            retrying={flow.retrying}
            onRetry={flow.retryNow}
          />
        ) : null}
        <SubmissionStatus state={state} />
      </CheckinForm>
    );

  return (
    <>
      {screen}

      {/*
        §13 — one widget for the whole flow, in normal document flow rather
        than a modal, and mounted on every screen: a retry from the pending
        screen has to be able to show an interactive challenge (L-003).
        It renders nothing until Cloudflare decides interaction is needed.
      */}
      <div
        ref={flow.turnstileContainerRef}
        className="mx-auto flex w-full max-w-[480px] justify-center px-5 empty:hidden"
      />

      {/* Never while a submission is in flight: a reload here would turn a
          finishing check-in into a recovery case. */}
      {updateReady && !inFlight ? <UpdatePrompt onApply={applyUpdate} /> : null}
    </>
  );
}
